import {
  createRequest,
  createRequestNoStringifyBody,
  handleErrorCatchLog,
  isProdSite,
} from '../../Helpers/GenericHelpers'
import { retrieveSelectedArticleKey } from '../../Helpers/ArticlesHelper'
import { imagesSectionId } from '../../Constants/Configuration'
import { getTokenFromSessionStorage } from 'Helpers/TokenHelper'
import { Image } from 'Slices/PIM/ImagesSlices'
import { RcFile } from 'antd/lib/upload'

const getImageFromUrl = url => {
  return url
}

const getImagesAndGalleries = (articleId: number) => async (dispatch, getState) => {
  const state = getState()
  const gridEnable: boolean =
    state.present.dynamicGrid.gridLayout.findIndex(gridElement => gridElement.i === imagesSectionId) !== -1
  const token = getTokenFromSessionStorage()

  if (
    !state.present.configuration.isImagesVisible ||
    !state.present.article.serverConfig ||
    state.present.article.rowKeysSelected[0] !== articleId ||
    !gridEnable ||
    !token
  ) {
    return []
  }

  try {
    const response = await createRequest(
      '/api/articles/' + articleId + '/images?config=' + state.present.article.serverConfig,
      'GET',
      undefined,
      token,
    )
    return await response.json()
  } catch (err) {
    handleErrorCatchLog(err, 'Failed retrieve images')
  }
}

const saveImageChanges = () => (dispatch, getState) => {
  const state = getState()
  const rowKeysSelected = state.present.article.rowKeysSelected
  const token = getTokenFromSessionStorage()

  const articleKey: any = retrieveSelectedArticleKey(rowKeysSelected)

  if (!state.present.configuration.isImagesVisible || !state.present.article.serverConfig || !articleKey || !token) {
    return new Promise(resolve => {
      resolve([])
    })
  }

  const body = {
    galleries: state.present.images.galleries,
  }

  return createRequest(
    '/api/articles/' + articleKey + '/updateImagesAndGalleries?config=' + state.present.article.serverConfig,
    'POST',
    body,
    token,
  )
    .then(response => response.json())
    .catch(err => {
      handleErrorCatchLog(err, 'Failed to save images changes')
    })
}

const updateImageAndSaveAsNew =
  (imageId: number, inlineSrc: string) =>
  async (_, getState): Promise<Image> => {
    const state = getState()
    const rowKeysSelected = state.present.article.rowKeysSelected
    const articleKey: any = retrieveSelectedArticleKey(rowKeysSelected)
    const token = getTokenFromSessionStorage()

    if (!state.present.configuration.isImagesVisible || !state.present.article.serverConfig || !articleKey || !token) {
      return undefined
    }

    const url = `/api/articles/${articleKey}/UpdateImageAndSaveAsNew/${imageId}?config=${state.present.article.serverConfig}`
    const body = {
      inlineSrc,
      overwrite: false,
    }
    try {
      const response = await createRequest(url, 'POST', body, token)
      return await response.json()
    } catch (err) {
      handleErrorCatchLog(err, 'Failed to update image')
    }
  }

const deleteImage =
  (imageId: number) =>
  async (_, getState): Promise<boolean> => {
    const state = getState()
    const rowKeysSelected = state.present.article.rowKeysSelected
    const token = getTokenFromSessionStorage()
    const articleKey: any = retrieveSelectedArticleKey(rowKeysSelected)

    if (
      !state.present.configuration.isImagesVisible ||
      !state.present.article.serverConfig ||
      !articleKey ||
      !imageId ||
      !token
    ) {
      return false
    }

    const body = {
      imageIds: [imageId],
    }

    try {
      const response = await createRequest(
        '/api/articles/' + articleKey + '/deleteImages/?config=' + state.present.article.serverConfig,
        'POST',
        body,
        token,
        true,
        true,
      )
      const result = await response.json()
      return result.success
    } catch (err) {
      handleErrorCatchLog(err, 'Failed to delete image')
    }
  }

const getImageSrc =
  (imageId: number) =>
  async (_, getState): Promise<string> => {
    const state = getState()
    const rowKeysSelected = state.present.article.rowKeysSelected
    const token = getTokenFromSessionStorage()
    const articleKey: any = retrieveSelectedArticleKey(rowKeysSelected)

    if (
      !state.present.configuration.isImagesVisible ||
      !state.present.article.serverConfig ||
      !articleKey ||
      !imageId ||
      !token
    ) {
      return ''
    }

    const url = `/api/articles/${articleKey}/GetBase64Image/${imageId}/?config=${state.present.article.serverConfig}`
    try {
      const request = await createRequest(url, 'GET', null, token, true, true)
      return await request.text()
    } catch (err) {
      handleErrorCatchLog(err, 'Failed to getBase64Image')
    }
  }

const uploadImage =
  (file: RcFile) =>
  async (_, getState): Promise<Image> => {
    const state = getState()
    const rowKeysSelected = state.present.article.rowKeysSelected
    const token = getTokenFromSessionStorage()
    const articleKey: any = retrieveSelectedArticleKey(rowKeysSelected)

    if (!state.present.configuration.isImagesVisible || !state.present.article.serverConfig || !articleKey || !token) {
      return undefined
    }

    const url = `/api/articles/${articleKey}/uploadImage/?config=${state.present.article.serverConfig}`

    let formData = new FormData()
    formData.append('formFile', file) // Must match the api parameter

    try {
      const response = await createRequestNoStringifyBody(url, 'POST', formData, token, true, true)
      return await response.json()
    } catch (err) {
      handleErrorCatchLog(err, 'Failed to upload image')
    }
  }

export {
  getImageFromUrl,
  getImagesAndGalleries,
  saveImageChanges,
  updateImageAndSaveAsNew,
  deleteImage,
  getImageSrc,
  uploadImage,
}
